import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth } from "@/App";
import { communityApi } from "@/lib/community";
import { Badge } from "@/components/ui/badge";
import { Users, MessageSquare, BellOff, Loader2, Sparkles } from "lucide-react";
import EmptyState from "@/components/EmptyState";

// WhatsApp/Telegram-style landing for the 3 public groups.
// Premium card layout — large cover image + circular group icon overlay + meta strip.

const ago = (iso, lang) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60)        return lang === "ar" ? "الآن" : "just now";
  const min = Math.floor(sec / 60);
  if (min < 60)        return lang === "ar" ? `قبل ${min} د` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)         return lang === "ar" ? `قبل ${hr} س` : `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7)           return lang === "ar" ? `قبل ${d} يوم` : `${d}d ago`;
  return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US");
};

export default function Community() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    let mounted = true;
    const load = async () => {
      try {
        const data = await communityApi.groups(token);
        if (mounted) setGroups(data);
      } catch { /* silent — polling will retry */ }
    };
    load();
    const interval = setInterval(load, 8000);   // refresh unread / last activity every 8s
    return () => { mounted = false; clearInterval(interval); };
  }, [token, navigate]);

  return (
    <DashboardLayout title={t("المجتمع", "Community")}>
      <div className="space-y-6" data-testid="community-page">
        {/* Hero strip */}
        <div className="relative glass-card rounded-3xl overflow-hidden p-5 lg:p-7 bg-luxury">
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] bg-blue-500/15 blur-[120px] rounded-full" />
          <div className="relative">
            <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 mb-2 border-yellow-500/30">
              <Sparkles size={12} className="ml-1" /> {t("مجتمع المغتربين العرب", "Arab Expat Community")}
            </Badge>
            <h2 className="text-2xl lg:text-4xl font-bold text-luxury">{t("انضمّ إلى مجموعات المجتمع", "Join the community groups")}</h2>
            <p className="text-slate-300 max-w-2xl text-sm lg:text-base mt-2">
              {t(
                "3 مجموعات رسمية فقط، آمنة وموثوقة، يديرها فريق التطبيق. تواصل مع جالية المغتربين العرب في أمريكا بسهولة كأنك تستخدم واتساب أو تيليجرام.",
                "Three official, trusted groups managed by the team. Connect with the Arab expat community in America — as easy as WhatsApp or Telegram.",
              )}
            </p>
          </div>
        </div>

        {/* Cards */}
        {!groups ? (
          <div className="glass-card rounded-2xl p-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-yellow-500" />
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={Users}
            title_ar="لا توجد مجموعات متاحة الآن"
            title_en="No groups available yet"
            body_ar="انضم لمجتمع عرب سند للتواصل مع آلاف العرب في أمريكا. جرّب الرسائل المباشرة أو الأصدقاء."
            body_en="Join the Arab Sanad community to connect with thousands of Arabs in America. Try direct messages or friends."
            cta_ar="الرسائل والأصدقاء"
            cta_en="Messages & Friends"
            onCta={() => navigate("/messages")}
            testid="community-empty"
          />
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="community-groups-grid">
            {groups.map((g) => (
              <button
                key={g.slug}
                onClick={() => navigate(`/community/${g.slug}`)}
                className="group relative text-right bg-slate-900/60 hover:bg-slate-900 border border-slate-800 hover:border-yellow-500/40 rounded-3xl overflow-hidden transition-all shadow-lg shadow-black/30"
                data-testid={`community-card-${g.slug}`}
                dir={language === "ar" ? "rtl" : "ltr"}
              >
                {/* Cover */}
                <div className="relative h-32 lg:h-40 overflow-hidden">
                  <img src={g.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                       onError={(e) => { e.target.style.display = "none"; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                  {/* Mute pill */}
                  {g.muted && (
                    <div className="absolute top-3 left-3 bg-slate-900/80 backdrop-blur text-slate-300 text-[10px] rounded-full px-2 py-1 inline-flex items-center gap-1" data-testid={`community-mute-${g.slug}`}>
                      <BellOff size={10} />{t("مكتومة", "Muted")}
                    </div>
                  )}
                  {/* Unread badge */}
                  {g.unread_count > 0 && (
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white text-[11px] font-bold rounded-full px-2.5 py-1 shadow-lg shadow-emerald-500/40" data-testid={`community-unread-${g.slug}`}>
                      {g.unread_count > 99 ? "99+" : g.unread_count} {t("جديدة", "new")}
                    </div>
                  )}
                  {/* Group icon overlay */}
                  <div
                    className="absolute -bottom-7 right-5 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-xl shadow-black/40 border-4 border-slate-900"
                    style={{ background: `linear-gradient(135deg, ${g.accent}, #0f172a)` }}
                  >
                    <span>{g.emoji}</span>
                  </div>
                </div>

                {/* Body */}
                <div className="px-5 pt-9 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-bold text-white truncate">{language === "ar" ? g.name_ar : g.name_en}</h3>
                    <span className="text-[11px] text-slate-500 flex-shrink-0">{ago(g.last_message_at, language)}</span>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2 mt-1 min-h-[2.4em]">
                    {language === "ar" ? g.description_ar : g.description_en}
                  </p>

                  {/* Last message preview */}
                  {g.last_message_preview ? (
                    <div className="mt-3 flex items-center gap-2 text-xs text-slate-400 bg-slate-900/60 border border-slate-800 rounded-xl px-3 py-2">
                      <MessageSquare size={12} className="text-yellow-500 flex-shrink-0" />
                      <span className="truncate">{g.last_message_preview}</span>
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-slate-500 italic">{t("لا توجد رسائل بعد — كن أول من يكتب", "No messages yet — be the first")}</div>
                  )}

                  {/* Meta strip */}
                  <div className="mt-3 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-slate-400">
                    <span className="inline-flex items-center gap-1" data-testid={`community-members-${g.slug}`}>
                      <Users size={11} className="text-emerald-400" />
                      {g.members_count.toLocaleString(language === "ar" ? "ar-EG" : "en-US")} {t("عضو", "members")}
                    </span>
                    <span className="text-yellow-300 font-semibold opacity-80 group-hover:opacity-100">
                      {t("افتح المحادثة", "Open chat")} →
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
